import { apolloClient } from "@App/graphql/client";
import {
  GET_ORGANIZATION_PREMIUM_SERVICES,
  type OrganizationPremiumServicesQuery,
} from "@App/graphql/queries/getOrganizationPremiumServices";
import { actionCreators, type State, store } from "@App/state";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const usePremiumServiceParameters = () => {
  const dispatch = useDispatch();
  const { addPremiumService } = bindActionCreators(actionCreators, dispatch);
  const [searchParams] = useSearchParams();
  const driverID = useSelector((state: State) => state.auth?.driverData?.uuid);
  const networkID = useSelector((state: State) => state.auth?.driverData?.network?.id);

  const organization = useSelector((state: State) => state.orders.organization);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const presetFilters = useSelector((state: State) => state.filters.presetFilters);

  const premiumServiceQuery = searchParams.get("premiumServices");
  // old query parameter for premium services, kept for backwards compatibility
  const premiumServiceQueryOld = searchParams.get("premiumservices");
  const serviceQuery = parseInt(searchParams.get("service") ?? "0", 10);

  const splitPremiumservicesCompatibility = () => {
    if (premiumServiceQuery) {
      return premiumServiceQuery.split(",");
    } else if (premiumServiceQueryOld) {
      return premiumServiceQueryOld.split(",");
    }
    return [];
  };

  useEffect(() => {
    if (serviceQuery && (premiumServiceQuery || premiumServiceQueryOld)) {
      const premiumServicesToSet = splitPremiumservicesCompatibility();

      apolloClient
        .query<OrganizationPremiumServicesQuery>({
          query: GET_ORGANIZATION_PREMIUM_SERVICES,
          variables: {
            organization: organization.id,
            services: [serviceQuery],
            ...(presetFilters &&
              presetFilters.length > 0 && {
                presetFilters: presetFilters,
              }),
            driver: driverID ?? null,
            network: networkID ?? null,
          },
        })
        .then((result) => {
          result.data.organization.premium_services?.forEach((orgPremiumService) => {
            if (premiumServicesToSet.some((p) => parseInt(p) == orgPremiumService.id)) {
              const isSet = premiumServices.some((p) => p.id == orgPremiumService.id);
              if (!isSet) {
                addPremiumService(orgPremiumService);
              }
            }
          });
        });
    }
  }, []);

  const premiumServiceQueryParams = (): Record<string, string> => {
    return {
      // add premiumServices if existing
      ...(premiumServices &&
        premiumServices.length > 0 && {
          premiumServices: store
            .getState()
            .orders.premiumServices.map((premiumService) => premiumService.id)
            .join(","),
        }),
    };
  };

  return { premiumServiceQueryParams };
};
